<template>
    <div class="task-schedule-picker">
        <div ref="triggerRef" class="task-schedule-trigger" :class="{ disabled, open: isOpen }">
            <button type="button" :disabled="disabled" @click="openMenu('start')">
                <img :src="calendarIcon" class="calendar-icon" alt="" />
                <span>{{ formatShort(startDate) || $t('RecurringTask.start') }}</span>
            </button>
            <span class="arrow">→</span>
            <button type="button" :disabled="disabled" @click="openMenu('due')">
                <img :src="calendarIcon" class="calendar-icon" alt="" />
                <span>{{ formatShort(dueDate) || $t('RecurringTask.due') }}</span>
            </button>
            <button
                v-if="!disabled && (asDate(startDate) || asDate(dueDate))"
                type="button"
                class="clear-btn trigger-clear"
                :title="$t('RecurringTask.clear')"
                @click.stop="clearAll"
            >×</button>
        </div>

        <Teleport to="body">
            <div v-if="isOpen" ref="menuRef" class="task-schedule-menu" :style="menuStyle">
                <div class="task-schedule-inputs">
                    <div
                        v-for="field in ['start', 'due']"
                        :key="field"
                        class="date-input"
                        :class="{ active: activeField === field }"
                        @click="setActiveField(field)"
                    >
                        <img :src="calendarIcon" class="calendar-icon" alt="" />
                        <template v-if="pending[field]">
                            <span class="date-text">{{ formatFull(pending[field]) }}</span>
                            <button type="button" class="clear-btn" :title="$t('RecurringTask.clear')" @click.stop="clearField(field)">×</button>
                        </template>
                        <span v-else class="placeholder">{{ $t(field === 'start' ? 'RecurringTask.start_date' : 'RecurringTask.due_date') }}</span>
                    </div>
                </div>

                <div class="task-schedule-body">
                    <div class="task-schedule-side">
                        <div class="task-schedule-presets">
                            <button v-for="preset in presets" :key="preset.key" type="button" @click="pickDate(preset.date)">
                                <span>{{ $t(`RecurringTask.${preset.key}`) }}</span>
                                <small>{{ preset.caption }}</small>
                            </button>
                        </div>
                        <div class="task-schedule-recurring">
                            <slot name="action-extra"></slot>
                        </div>
                    </div>

                    <div class="task-schedule-calendar">
                        <div class="calendar-head">
                            <strong>{{ monthLabel }}</strong>
                            <div class="calendar-nav">
                                <button type="button" class="today-btn" @click="goToToday">{{ $t('RecurringTask.today') }}</button>
                                <button type="button" class="nav-btn" aria-label="Previous month" @click="shiftMonth(-1)">
                                    <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 10l4-4 4 4" /></svg>
                                </button>
                                <button type="button" class="nav-btn" aria-label="Next month" @click="shiftMonth(1)">
                                    <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6l4 4 4-4" /></svg>
                                </button>
                            </div>
                        </div>
                        <div class="calendar-grid weekdays">
                            <span v-for="day in weekdayLabels" :key="day">{{ day }}</span>
                        </div>
                        <div v-for="(week, wi) in weeks" :key="wi" class="calendar-grid week">
                            <button
                                v-for="cell in week"
                                :key="cell.time"
                                type="button"
                                class="day"
                                :class="cell.classes"
                                @click="pickDate(cell.date)"
                            >
                                <span>{{ cell.date.getDate() }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from "vue";
import calendarIcon from "@/assets/images/svg/calendar.svg";

const props = defineProps({
    startDate: { default: null },
    dueDate: { default: null },
    disabled: { type: Boolean, default: false },
});
const emit = defineEmits(["select", "closed"]);

const MENU_WIDTH = 540;
const triggerRef = ref(null);
const menuRef = ref(null);
const isOpen = ref(false);
const activeField = ref("start");
const viewMonth = ref(startOfMonth(new Date()));
const menuStyle = ref({});
const pending = reactive({ start: null, due: null });
let resizeObserver = null;

// Task dates are date-only
function asDate(value) {
    if (!value) return null;
    const date = value?.seconds ? new Date(value.seconds * 1000) : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
}
function dayOf(date) {
    return date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
}
function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}
function atDay(offset) {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + offset);
    return date;
}
function nextWeekday(weekday, nextWeek = false) {
    const today = atDay(0);
    let offset = (weekday - today.getDay() + 7) % 7;
    if (nextWeek || offset === 0) offset += 7;
    return atDay(offset);
}
const sameDay = (a, b) => !!a && !!b && dayOf(a).getTime() === dayOf(b).getTime();
const sameValue = (a, b) => (a ? a.getTime() : null) === (b ? b.getTime() : null);

const syncFromProps = () => {
    pending.start = asDate(props.startDate);
    pending.due = asDate(props.dueDate);
};
watch(() => [props.startDate, props.dueDate], syncFromProps, { immediate: true, deep: true });

const shortFormatter = new Intl.DateTimeFormat("en-US", { month: "numeric", day: "numeric" });
const fullFormatter = new Intl.DateTimeFormat("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
const weekdayFormatter = new Intl.DateTimeFormat("en-US", { weekday: "short" });
const dayMonthFormatter = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });
const monthFormatter = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });
const weekdayLabels = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const formatShort = (value) => (asDate(value) ? shortFormatter.format(asDate(value)) : "");
const formatFull = (date) => fullFormatter.format(date);

const presets = computed(() => [
    { key: "today", date: atDay(0), caption: weekdayFormatter.format(atDay(0)) },
    { key: "tomorrow", date: atDay(1), caption: weekdayFormatter.format(atDay(1)) },
    { key: "this_weekend", date: nextWeekday(6), caption: weekdayFormatter.format(nextWeekday(6)) },
    { key: "next_week", date: nextWeekday(1, true), caption: weekdayFormatter.format(nextWeekday(1, true)) },
    { key: "next_weekend", date: nextWeekday(6, true), caption: dayMonthFormatter.format(nextWeekday(6, true)) },
    { key: "two_weeks", date: atDay(14), caption: dayMonthFormatter.format(atDay(14)) },
    { key: "four_weeks", date: atDay(28), caption: dayMonthFormatter.format(atDay(28)) },
]);

const monthLabel = computed(() => monthFormatter.format(viewMonth.value));
const weeks = computed(() => {
    const first = viewMonth.value;
    const gridStart = new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay());
    const today = atDay(0);
    const start = dayOf(pending.start);
    const due = dayOf(pending.due);
    const rows = [];
    for (let w = 0; w < 6; w++) {
        const row = [];
        for (let d = 0; d < 7; d++) {
            const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + w * 7 + d);
            const time = date.getTime();
            const inRange = start && due && time >= start.getTime() && time <= due.getTime();
            row.push({
                date,
                time,
                classes: {
                    muted: date.getMonth() !== first.getMonth(),
                    today: sameDay(date, today),
                    "in-range": inRange,
                    "range-start": inRange && sameDay(date, start),
                    "range-end": inRange && sameDay(date, due),
                    selected: sameDay(date, start) || sameDay(date, due),
                },
            });
        }
        rows.push(row);
    }
    return rows;
});

function positionMenu() {
    const rect = triggerRef.value?.getBoundingClientRect();
    if (!rect) return;
    const minWidth = Math.min(MENU_WIDTH, window.innerWidth - 16);
    // The menu grows when the recurrence editor opens, so measure it rather than assume MENU_WIDTH
    const width = Math.max(minWidth, menuRef.value?.offsetWidth || 0);
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    const menuHeight = menuRef.value?.offsetHeight || 420;
    const below = rect.bottom + 6;
    const top = below + menuHeight > window.innerHeight - 8 ? Math.max(8, rect.top - menuHeight - 6) : below;
    menuStyle.value = { top: `${top}px`, left: `${left}px`, minWidth: `${minWidth}px`, maxWidth: "calc(100vw - 16px)" };
}

function openMenu(field) {
    if (props.disabled) return;
    syncFromProps();
    setActiveField(field);
    isOpen.value = true;
    nextTick(() => {
        positionMenu();
        resizeObserver = new ResizeObserver(positionMenu);
        resizeObserver.observe(menuRef.value);
    });
    document.addEventListener("mousedown", onOutsideClick, true);
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
}

function closeMenu() {
    if (!isOpen.value) return;
    isOpen.value = false;
    resizeObserver?.disconnect();
    resizeObserver = null;
    document.removeEventListener("mousedown", onOutsideClick, true);
    window.removeEventListener("resize", positionMenu);
    window.removeEventListener("scroll", positionMenu, true);
    emit("closed");
}

function onOutsideClick(event) {
    if (menuRef.value?.contains(event.target) || triggerRef.value?.contains(event.target)) return;
    closeMenu();
}

function setActiveField(field) {
    activeField.value = field;
    viewMonth.value = startOfMonth(pending[field] || pending.start || pending.due || atDay(0));
}

function shiftMonth(step) {
    viewMonth.value = new Date(viewMonth.value.getFullYear(), viewMonth.value.getMonth() + step, 1);
}

function goToToday() {
    viewMonth.value = startOfMonth(atDay(0));
}

// Picks go to the field being edited; start then moves on to due, due finishes the range
function pickDate(date) {
    const field = activeField.value;
    const picked = dayOf(date);
    pending[field] = picked;
    if (field === "start" && pending.due && pending.due < picked) pending.due = new Date(picked);
    if (field === "due" && pending.start && pending.start > picked) pending.start = new Date(picked);
    commit();
    if (field === "start") setActiveField("due");
    else closeMenu();
}

function clearField(field) {
    pending[field] = null;
    setActiveField(field);
    commit();
}

function clearAll() {
    pending.start = null;
    pending.due = null;
    commit();
    closeMenu();
}

function commit() {
    const changed = !sameValue(pending.start, asDate(props.startDate)) || !sameValue(pending.due, asDate(props.dueDate));
    if (!changed) return;
    emit("select", {
        startDate: pending.start ? new Date(pending.start) : null,
        dueDate: pending.due ? new Date(pending.due) : null,
    });
}

onBeforeUnmount(closeMenu);
defineExpose({ closeMenu });
</script>

<style scoped>
.task-schedule-picker { width: 100%; }
.task-schedule-trigger { display: flex; align-items: center; gap: 4px; width: 100%; min-height: 36px; padding: 0 4px; border: 1px solid transparent; border-radius: 7px; }
.task-schedule-trigger:not(.disabled):hover,
.task-schedule-trigger.open { border-color: #dfe5ec; background: #f5f7fa; }
.task-schedule-trigger button { display: flex; align-items: center; gap: 7px; padding: 6px; border: 0; border-radius: 5px; background: transparent; color: #172b4d; cursor: pointer; font-family: 'Inter', sans-serif; font-size: 13px; }
.task-schedule-trigger button:hover:not(:disabled) { background: #e9eef4; }
.task-schedule-trigger button:disabled { cursor: not-allowed; opacity: .55; }
.task-schedule-trigger span { height: auto; padding: 0; border: 0; border-radius: 0; font: inherit; line-height: normal; }
.task-schedule-trigger .arrow { color: #8c98a7; }
.task-schedule-trigger .trigger-clear { margin-left: auto; padding: 0 6px; color: #8c98a7; font-size: 16px; visibility: hidden; }
.task-schedule-trigger:hover .trigger-clear,
.task-schedule-trigger.open .trigger-clear { visibility: visible; }
.calendar-icon { width: 14px; height: 14px; flex: 0 0 14px; }
</style>

<style>
.task-schedule-menu { position: fixed; z-index: 10000; overflow: hidden; border: 1px solid #dfe5ec; border-radius: 12px; background: #fff; box-shadow: 0 18px 48px rgba(15, 35, 58, .18); color: #172b4d; font-family: 'Inter', sans-serif; }
.task-schedule-menu button { font-family: inherit; }
.task-schedule-menu span { height: auto; padding: 0; border: 0; border-radius: 0; line-height: normal; }
.task-schedule-menu .clear-btn { display: flex; align-items: center; justify-content: center; width: 18px; height: 18px; padding: 0; border: 0; border-radius: 50%; background: transparent; color: #8c98a7; cursor: pointer; font-size: 15px; line-height: 1; }
.task-schedule-menu .clear-btn:hover { background: #e9eef4; color: #172b4d; }
.task-schedule-menu .task-schedule-inputs { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding: 9px; border-bottom: 1px solid #e5e9ef; }
.task-schedule-menu .date-input { display: flex; align-items: center; gap: 6px; min-width: 0; min-height: 36px; padding: 0 8px 0 10px; border: 1px solid #dfe5ec; border-radius: 7px; background: #f8fafc; cursor: pointer; font-size: 13px; font-weight: 500; }
.task-schedule-menu .date-input.active { border-color: #0875de; background: #fff; box-shadow: 0 0 0 2px rgba(8, 117, 222, .15); }
.task-schedule-menu .date-input .placeholder { color: #8c98a7; font-weight: 400; }
.task-schedule-menu .task-schedule-body { display: flex; align-items: stretch; }
.task-schedule-menu .task-schedule-side { display: flex; flex-direction: column; min-width: 210px; border-right: 1px solid #e5e9ef; }
.task-schedule-menu .task-schedule-side:has(.task-recurrence-editor) { width: 300px; }
.task-schedule-menu .task-schedule-presets { display: flex; flex-direction: column; flex: 1; padding: 6px 0; }
.task-schedule-menu .task-schedule-presets button { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 32px; padding: 0 15px; border: 0; background: transparent; color: #172b4d; cursor: pointer; font-size: 13px; font-weight: 500; text-align: left; }
.task-schedule-menu .task-schedule-presets button:hover { background: #f2f7fc; color: #0875de; }
.task-schedule-menu .task-schedule-presets small { color: #8c98a7; font-size: 11px; font-weight: 400; white-space: nowrap; }
.task-schedule-menu .task-schedule-recurring .task-recurrence-calendar-trigger { width: 100%; min-height: 40px; padding: 0 15px; border: 0; border-radius: 0; background: transparent; }
.task-schedule-menu .task-schedule-calendar { flex: 1; min-width: 264px; padding: 10px 12px 12px; }
.task-schedule-menu .calendar-head { display: flex; align-items: center; justify-content: space-between; height: 34px; margin-bottom: 4px; padding-left: 4px; }
.task-schedule-menu .calendar-head strong { font-size: 13px; font-weight: 600; }
.task-schedule-menu .calendar-nav { display: flex; align-items: center; gap: 2px; }
.task-schedule-menu .today-btn { height: 26px; padding: 0 8px; border: 0; border-radius: 6px; background: transparent; color: #526174; cursor: pointer; font-size: 12px; font-weight: 500; }
.task-schedule-menu .nav-btn { display: flex; align-items: center; justify-content: center; width: 26px; height: 26px; border: 0; border-radius: 6px; background: transparent; cursor: pointer; }
.task-schedule-menu .today-btn:hover,
.task-schedule-menu .nav-btn:hover { background: #f2f5f8; }
.task-schedule-menu .nav-btn svg { width: 14px; height: 14px; fill: none; stroke: #526174; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.task-schedule-menu .calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); }
.task-schedule-menu .calendar-grid.weekdays span { padding: 4px 0; color: #8c98a7; font-size: 11px; font-weight: 500; text-align: center; }
.task-schedule-menu .calendar-grid.week { margin: 2px 0; }
.task-schedule-menu .day { display: flex; align-items: center; justify-content: center; height: 32px; padding: 0; border: 0; background: transparent; cursor: pointer; }
.task-schedule-menu .day span { display: flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 7px; color: #172b4d; font-size: 12px; font-weight: 500; }
.task-schedule-menu .day:hover span { background: #edf4fb; }
.task-schedule-menu .day.muted span { color: #b3bcc7; }
.task-schedule-menu .day.in-range { background: #e8f2fc; }
.task-schedule-menu .day.in-range:nth-child(7n + 1),
.task-schedule-menu .day.range-start { border-top-left-radius: 8px; border-bottom-left-radius: 8px; }
.task-schedule-menu .day.in-range:nth-child(7n),
.task-schedule-menu .day.range-end { border-top-right-radius: 8px; border-bottom-right-radius: 8px; }
.task-schedule-menu .day.selected span { border: 1px solid #0875de; background: #fff; color: #0875de; font-weight: 600; }
.task-schedule-menu .day.today span { border-radius: 50%; background: #e5484d; color: #fff; font-weight: 600; }
.task-schedule-menu .day.today.selected span { border-color: #e5484d; }
@media (max-width: 600px) {
    .task-schedule-menu .task-schedule-inputs { grid-template-columns: 1fr; }
    .task-schedule-menu .task-schedule-body { flex-direction: column-reverse; }
    .task-schedule-menu .task-schedule-side { border-top: 1px solid #e5e9ef; border-right: 0; }
    .task-schedule-menu .task-schedule-presets { display: grid; grid-auto-columns: 145px; grid-auto-flow: column; overflow-x: auto; padding: 6px; }
}
</style>
